import { Injectable, inject } from '@angular/core';
import { SessionService } from './session';
import { MinutesService } from '../dataServices/minute';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { CustomMenuItem } from '../../models/Interfaces/CustomMenuItem';

@Injectable({
  providedIn: 'root',
})
export class ReadOnlyUserService {
  private sessionService = inject(SessionService);
  private minutesService = inject(MinutesService);
  /**
   * Check if user is read-only
   */
  isReadOnlyUser(): boolean {
    const user = this.sessionService.getCurrentUser();
    return user?.isReadOnly!;
  }

  /**
   * Check if user can access a specific route
   */
  canAccessRoute(routePath: string) {
    const user = this.sessionService.currentUser();

    // Non-read-only users can access everything
    if (!user?.isReadOnly) return true;

    // Extract the main path (remove /main/ prefix)
    const cleanPath = routePath.replace('/main/', '');

    // Allowed routes for read-only users
    const allowedRoutes = [
      'PersonalInformations', // Profile editing
      'minutes', // Minutes listing
      'dashboard', // Dashboard
      'logout', // Logout
      'notifications', // Notifications
      'settings',
      'minutes/:id', // Specific minute (checked separately)
    ];

    // Check if it's a minutes detail route
    if (cleanPath.startsWith('minutes/')) {
      const minuteId = cleanPath.split('/')[1];
      let canAccess = false;
      this.canAccessMinute(minuteId).then((access) => (canAccess = access));

      return canAccess;
    }

    // Check exact match
    for (const allowed of allowedRoutes) {
      if (allowed.includes(':')) {
        // Dynamic route pattern
        const pattern = allowed.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${pattern}$`);
        return regex.test(cleanPath);
      }
      if (cleanPath === allowed) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if user can access a specific minute
   */
  async canAccessMinute(minuteId: string): Promise<boolean> {
    const user = this.sessionService.currentUser();

    if (!user?.isReadOnly) return true;

    // Check if user is an attendee of this minute
    return await this.canInteractWithMinute(minuteId);
  }

  /**
   * Check if user is an attendee of a specific minute
   * Returns Observable for async checking
   */
  isAttendeeOfMinute(minuteId: string): Observable<boolean> {
    const user = this.sessionService.currentUser();
    if (!user) {
      return of(false);
    }

    return this.minutesService
      .getMinuteAttendees({
        MinuteId: Number(minuteId),
        PageNumber: 1,
        PageSize: 100,
      })
      .pipe(
        map((response) => {
          if (response?.data?.items) {
            return response.data.items.some(
              (attendee) => attendee.userId === user.userId,
            );
          }
          return false;
        }),
        catchError(() => of(false)),
      );
  }

  /**
   * Check if user can interact with a specific minute
   * Returns Promise for easier use in directives
   */
  async canInteractWithMinute(minuteId: string): Promise<boolean> {
    if (!this.isReadOnlyUser()) {
      return true;
    }

    try {
      const isAttendee = await firstValueFrom(
        this.isAttendeeOfMinute(minuteId),
      );
      return isAttendee;
    } catch {
      return false;
    }
  }

  /**
   * Check if a menu item should be shown for read-only users
   */
  canShowMenuItem(menuItem: CustomMenuItem): boolean {
    // Non-read-only users can see everything
    if (!this.isReadOnlyUser()) {
      return true;
    }

    // Check if main route is accessible
    if (menuItem.routerLink && !this.canAccessRoute(menuItem.routerLink)) {
      return false;
    }

    // Check submenu items
    if (menuItem.subMenu && menuItem.subMenu.length > 0) {
      // Check if any submenu items are accessible
      const hasAccessibleSubItems = menuItem.subMenu.some((subItem: any) =>
        this.canAccessRoute(subItem.routerLink),
      );
      return hasAccessibleSubItems;
    }

    return true;
  }

  /**
   * Filter submenu items for read-only users
   */
  filterSubMenuItems(subMenu: any[]): any[] {
    if (!this.isReadOnlyUser()) {
      return subMenu;
    }

    return subMenu.filter((subItem) => this.canAccessRoute(subItem.routerLink));
  }
}
